import { useState, useEffect, useContext, useMemo } from "react";
import Screen from "../components/Screen";
import { useParams } from "react-router";
import Rate from "../components/Rate";
import Rated from "../components/Rated";
import { ThanksForRating } from "../components/Icons";
import { HotelContext } from "../context/hotel";
import { getCDNImage } from "../globals";
import { useReviews } from "../hooks/useReviews";
import { T } from "react-translator-component";
import {
  getReviewFlowTheme,
  REVIEW_SETTING_TYPES,
  getEffectiveReviewSettingType,
  normalizeReviewFlow,
  resolveReviewFlowNodeTheme,
} from "../components/Reviews/reviewQuestionBlocks";

export const Review = () => {
  const { id } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const { data, previewMode, previewReview } = useContext(HotelContext);
  const isPreviewReview =
    !!previewMode &&
    !!previewReview &&
    (String(previewReview?.settingId || "") === String(id || "") || String(id || "") === "preview");
  const liveReviewsBundle = useReviews(previewMode ? null : id);
  const reviewsBundle = useMemo(() => {
    if (!isPreviewReview || !previewReview?.setting) {
      return liveReviewsBundle;
    }

    return {
      loading: false,
      data: previewReview.setting,
      reviewFlow: normalizeReviewFlow(previewReview.setting?.questions),
      reviewSettingType: getEffectiveReviewSettingType(previewReview.setting),
      create: async () => "preview-review",
      update: async () => ({ status: true }),
      doneRating: false,
      setDoneRating: () => {},
      isPreview: true,
      previewSelectedNodeId: previewReview?.selectedNodeId || null,
      previewPlayMode: !!previewReview?.playMode,
      previewSimulatedUpsellingIds: Array.isArray(previewReview?.simulatedUpsellingIds)
        ? previewReview.simulatedUpsellingIds
        : [],
    };
  }, [isPreviewReview, liveReviewsBundle, previewReview]);

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (!isPreviewReview) {
      return;
    }
    setReview(null);
    setLoading(false);
  }, [
    isPreviewReview,
    previewReview?.selectedNodeId,
    previewReview?.playMode,
    previewReview?.simulatedUpsellingIds,
  ]);

  const backgroundPic = useMemo(() => {
    if (!data || !data.settings || !data.settings.homeSliderImages || data.settings.homeSliderImages.length === 0) {
      return null;
    }
    const randomIndex = Math.floor(Math.random() * data.settings.homeSliderImages.length);
    const image = data.settings.homeSliderImages[randomIndex];
    return image ? getCDNImage(image) : null;
  }, [data]);

  const isImmersiveReview = true;
  const previewSelectedNode = useMemo(() => {
    if (!isPreviewReview) {
      return null;
    }
    return reviewsBundle?.reviewFlow?.nodeById?.[reviewsBundle?.previewSelectedNodeId || ""] || null;
  }, [isPreviewReview, reviewsBundle]);
  const previewShellTheme = useMemo(() => {
    const flow = reviewsBundle?.reviewFlow;
    if (!flow) {
      return getReviewFlowTheme(null);
    }
    if (previewSelectedNode) {
      return resolveReviewFlowNodeTheme(flow, previewSelectedNode);
    }
    return getReviewFlowTheme(flow);
  }, [previewSelectedNode, reviewsBundle?.reviewFlow]);
  const isLandingPreviewInspect =
    !!previewSelectedNode &&
    previewSelectedNode.type === "landing_page" &&
    !!isPreviewReview &&
    !reviewsBundle?.previewPlayMode;

  const refresh = () => {
    if (previewMode) {
      setReview(null);
      setLoading(false);
      return;
    }
    const localVoteData = localStorage.getItem(`anonymousVoteData${id}`);
    if (localVoteData) {
      try {
        const parsed = JSON.parse(localVoteData);
        const hasStarOnly = typeof parsed.review === "number" && parsed.extraReviews == null;
        const hasLegacyFullSubmit =
          typeof parsed.review === "number" &&
          parsed.extraReviews != null &&
          Object.prototype.hasOwnProperty.call(parsed, "comment");
        const showRated =
          parsed.reviewSubmitted === true || hasStarOnly || hasLegacyFullSubmit;
        if (showRated) {
          setReview(parsed);
          try {
            sessionStorage.removeItem(`flowReviewRatingId_${id}`);
          } catch (e) {
            /* ignore */
          }
        } else {
          setReview(null);
        }
      } catch (e) {
        setReview(null);
      }
    }
    setLoading(false);
  };

  return (
    <Screen
      section="review"
      title={isImmersiveReview ? "" : T("review.screen_title")}
      showBackButton={!isImmersiveReview}
      backGroundImage={isImmersiveReview ? undefined : backgroundPic}
      LangSelector={!isImmersiveReview}
      immersive={isImmersiveReview}
    >
      {isImmersiveReview ? (
        <div
          className="d-flex flex-column w-100"
          style={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            background: isPreviewReview ? previewShellTheme.backgroundColor : isLandingPreviewInspect ? "#112709" : "#f8fafc",
            overflow: isPreviewReview || isLandingPreviewInspect ? "hidden" : undefined,
          }}
        >
          {loading || reviewsBundle.loading || (previewMode && !reviewsBundle.data) ? (
            <div
              className="d-flex flex-grow-1 justify-content-center align-items-center"
              style={{ minHeight: "50vh" }}
            >
              <div className="spinner-border " role="status" />
            </div>
          ) : review === undefined || review === null ? (
            <Rate
              key={`rate-${id}`}
              type="general"
              settingId={id}
              reviewsBundle={reviewsBundle}
              standaloneReviewFlow
              onRate={(nextReview) => {
                if (isPreviewReview) {
                  setReview(nextReview || { reviewSubmitted: true });
                  return;
                }
                refresh();
              }}
            />
          ) : (
            <div
              className="d-flex flex-column flex-grow-1 w-100"
              style={{ padding: "24px 20px calc(24px + env(safe-area-inset-bottom, 0px))" }}
            >
              <Rated key={`rate-${id}-r`} hasRated={review} fullBleed />
            </div>
          )}
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center flex-column"
          style={{
            background: "#FFF",
            opacity: 0.9,
            padding: 20,
            borderRadius: 10,
            width: "75%",
            marginTop: "15%",
            marginLeft: "auto",
            marginRight: "auto",
            border: "2px solid #988460",
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
          }}
        >
          <div
            className="mb-2 mt-2"
            style={{
              width: 240,
              height: 240,
              borderRadius: "50%",
              border: "3px solid #FFF",
              background: "#FFFFFF",
              position: "relative",
              overflow: "hidden",
              zIndex: 2,
            }}
          >
            <img
              src={getCDNImage(data.logo)}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "contain",
                display: "block",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
              alt=""
            />
          </div>

          <div style={{ marginTop: 10 }}>
            {loading || reviewsBundle.loading ? (
              <div className="w-100 text-center mt-4">
                <div className="loading">
                  <div className="spinner-border" role="status" />
                </div>
              </div>
            ) : review === undefined || review === null ? (
              <div className="text-center mt-4">
                <Rate
                  key={`rate-${id}`}
                  type="general"
                  settingId={id}
                  reviewsBundle={reviewsBundle}
                  onRate={() => {
                    refresh();
                  }}
                />
              </div>
            ) : (
              <div className="text-center mt-4">
                <Rated key={`rate-${id}-r`} hasRated={review} />
              </div>
            )}
          </div>
          {(review === undefined || review === null) && <ThanksForRating width={75} height={75} />}
        </div>
      )}
    </Screen>
  );
};
